"use client";

import React from "react";
import { Play, Funnel } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { SidebarProvider, Sidebar, SidebarContent, SidebarHeader, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductSortDropdown, ProductsFilter } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ShopGridConsumer() {
  const {
    products,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    isRefetching
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border shadow-sm">
            <div className="aspect-[4/3] animate-pulse bg-muted" />
            <CardContent className="space-y-4 p-6">
              <div className="h-4 w-1/4 rounded bg-muted animate-pulse" />
              <div className="h-6 w-3/4 rounded bg-muted animate-pulse" />
              <div className="h-6 w-1/2 rounded bg-muted animate-pulse" />
            </CardContent>
            <CardFooter className="p-6 pt-0">
              <div className="h-12 w-full rounded bg-muted animate-pulse" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
        <p className="mb-4 text-lg text-muted-foreground font-default">
          An error occurred while loading our collections.
        </p>
        <Button variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary/10" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
        <p className="text-lg text-muted-foreground font-default">
          No collars found matching your current filters.
        </p>
      </div>;
  }
  return <div className="flex flex-col space-y-12">
      <div className={`grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 transition-opacity duration-300 ${isRefetching ? "opacity-50 pointer-events-none" : "opacity-100"}`}>
        {products.map((product, i) => <Card key={product.id} data-index={i} className="group flex flex-col overflow-hidden border-none bg-card shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 rounded-lg">
            <Link to={product.link} className="relative block aspect-[4/3] overflow-hidden bg-muted">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-accent/20">
                  <Play className="h-12 w-12 text-muted-foreground/50" />
                </div>}
              
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-500 group-hover:bg-black/10 group-hover:opacity-100">
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/90 shadow-lg backdrop-blur-sm transition-transform group-hover:scale-110">
                  <Play className="ml-1 h-6 w-6 text-primary-foreground" fill="currentColor" />
                </div>
              </div>
            </Link>

            <CardContent className="flex flex-1 flex-col p-6">
              {product.categories && product.categories.length > 0 && <div className="mb-3 flex flex-wrap gap-2">
                  {product.categories.slice(0, 2).map((cat, idx) => <span key={cat.id} data-index={idx} className="text-xs font-semibold uppercase tracking-widest text-secondary">
                      {cat.name}
                    </span>)}
                </div>}
              
              <Link to={product.link} className="mb-4 block">
                <h3 className="font-default text-2xl font-bold leading-tight text-foreground transition-colors hover:text-primary line-clamp-2">
                  {product.title}
                </h3>
              </Link>

              <div className="mt-auto flex items-center justify-between pt-4">
                <span className="font-default text-2xl font-bold text-foreground">
                  ${product.price?.toFixed(2)}
                </span>
                {product.discounted_price && <span className="text-sm text-muted-foreground line-through font-default">
                    ${product.discounted_price.toFixed(2)}
                  </span>}
              </div>
            </CardContent>

            <CardFooter className="p-6 pt-0">
              <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-sm font-bold uppercase tracking-widest rounded-lg shadow-md transition-all" text="Add to Cart" showQuantity={false} />
            </CardFooter>
          </Card>)}
      </div>

      {totalPages > 1 && <Pagination className="pt-8 border-t border-border">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious onClick={prevPage} className={!hasPrev || isRefetching ? "pointer-events-none opacity-50" : "cursor-pointer font-default"} />
            </PaginationItem>
            <PaginationItem>
              <span className="px-4 text-sm font-medium text-muted-foreground font-default">
                Page {currentPage} of {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext onClick={nextPage} className={!hasNext || isRefetching ? "pointer-events-none opacity-50" : "cursor-pointer font-default"} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function ShopContent() {
  const current_wp_query_params = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(current_wp_query_params);
  return <section data-section-id="222329" id="shop-content" className="w-full bg-background text-foreground">
      <EcommerceProductsProvider wp_query={wp_query}>
        <SidebarProvider className="mx-auto max-w-7xl items-start md:py-12">
          
          <Sidebar variant="sidebar" collapsible="offcanvas" className="border-r-0 md:bg-transparent md:pt-4">
            <SidebarHeader className="px-4 py-6 md:hidden">
              <h2 className="font-default text-2xl font-bold text-foreground">Filters</h2>
            </SidebarHeader>
            <SidebarContent className="px-4 pb-12 pt-2 md:px-0 md:pr-8">
              <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
                <ProductsFilter />
              </div>
            </SidebarContent>
          </Sidebar>

          <SidebarInset className="min-h-[80vh] bg-transparent p-4 md:p-6 lg:p-8">
            <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="font-serif text-4xl font-bold text-foreground md:text-5xl tracking-tight">
                  Shop All Collars
                </h1>
                <p className="mt-2 text-muted-foreground font-default max-w-md">
                  Handcrafted beach-inspired designs for dogs who live the surf lifestyle.
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <SidebarTrigger className="flex h-10 items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-sm font-bold uppercase tracking-wider shadow-sm hover:bg-accent hover:text-accent-foreground md:hidden">
                  <Funnel className="h-4 w-4" />
                  Filters
                </SidebarTrigger>
                
                <div className="min-w-[200px]">
                  <ProductSortDropdown label="Sort By" />
                </div>
              </div>
            </header>

            <main>
              <ShopGridConsumer />
            </main>
          </SidebarInset>

        </SidebarProvider>
      </EcommerceProductsProvider>
    </section>;
}